import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import { formatInTimeZone } from 'date-fns-tz';
import {
    Building2,
    Check,
    CheckCircle2,
    Info,
    Lightbulb,
    Loader2,
    type LucideIcon,
    MessageCircle,
    UserPlus,
    UserRound,
    XCircle,
} from 'lucide-react';
import { Fragment, type ReactNode, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

import { StaffImessageNotificationBudgetSection } from '@apps/web/components/admin/staff_imessage_notification_budget_section';
import { StaffImessageProvisionDialog } from '@apps/web/components/admin/staff_imessage_provision_dialog';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import {
    ResizableHandle,
    ResizablePanel,
    ResizablePanelGroup,
} from '@apps/web/components/ui/resizable';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Sheet,
    SheetContent,
    SheetDescription,
    SheetHeader,
    SheetTitle,
} from '@apps/web/components/ui/sheet';
import { Switch } from '@apps/web/components/ui/switch';
import { getApiClient } from '@apps/web/lib/api_client';
import { toast } from '@apps/web/lib/app_notifications';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminImessageFilterOptions,
    useAdminImessageStaffDiagnostics,
    useAdminImessageThread,
    useAdminImessageThreads,
    useAdminOrganization,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import {
    type AdminImessageFilterOptionRead,
    type AdminImessageListItemRead,
    type AdminImessageThreadMessageRead,
    type AdminImessageThreadRead,
} from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/imessage')({
    head: () => ({
        meta: [
            {
                title: 'iMessage | Phoebe Admin',
            },
        ],
    }),
    component: ImessagePage,
    validateSearch: z.object({
        threadId: z.string().optional(),
    }),
});

const THREAD_PAGE_SIZE = 50;
// Mirrors MAX_THREAD_LIST_LIMIT on the API.
const MAX_THREAD_LIST_LIMIT = 500;
// Follow live updates only when the reader is already this close to the
// bottom of the transcript; never yank them out of scrolled-up history.
const FOLLOW_SCROLL_THRESHOLD_PX = 150;

const RUN_STATUS_COLORS: Record<string, string> = {
    running:
        'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    idle: 'bg-muted text-foreground',
    failed: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    completed:
        'bg-blue-100 text-blue-800 dark:bg-blue-950/50 dark:text-blue-300',
    cancelled: 'bg-muted text-foreground',
};

function shortTimeAgo(iso: string, nowMs: number, timezone: string): string {
    const diffMin = Math.floor(
        Math.max(0, nowMs - new Date(iso).getTime()) / 60_000,
    );
    if (diffMin < 1) {
        return 'now';
    }
    if (diffMin < 60) {
        return `${diffMin}m`;
    }
    if (diffMin < 24 * 60) {
        return `${Math.floor(diffMin / 60)}h`;
    }
    if (diffMin < 7 * 24 * 60) {
        return `${Math.floor(diffMin / (24 * 60))}d`;
    }
    return formatInTimeZone(new Date(iso), timezone, 'MMM d, yyyy');
}

function userInitials(name: string | null): string {
    if (!name) {
        return '?';
    }
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.charAt(0) ?? '';
    const second = parts.length > 1 ? (parts[1]?.charAt(0) ?? '') : '';
    const initials = `${first}${second}`.toUpperCase();
    return initials || '?';
}

function previewText(item: AdminImessageListItemRead): string {
    if (item.item_type === 'staff') {
        return 'No conversation yet';
    }
    if (!item.latest_message) {
        return 'No messages yet';
    }
    return item.latest_message.body_text ?? 'No text';
}

function ImessagePage() {
    const { threadId } = Route.useSearch();

    // Pin the page to the viewport (minus the 3.5rem mobile header) so the
    // panes scroll internally; the admin shell only sets min-h-svh, so an
    // unbounded page would grow and scroll the conversation list away.
    return (
        <div className="flex h-[calc(100svh-3.5rem)] max-h-[calc(100svh-3.5rem)] min-h-0 w-full flex-col overflow-hidden md:h-svh md:max-h-svh">
            <ResizablePanelGroup orientation="horizontal" className="h-full">
                <ResizablePanel defaultSize={30} minSize={20}>
                    <div className="border-border flex h-full min-h-0 flex-col border-r">
                        <ThreadList selectedThreadId={threadId} />
                    </div>
                </ResizablePanel>
                <ResizableHandle withHandle={true} />
                <ResizablePanel defaultSize={70} minSize={40}>
                    <ThreadDetail threadId={threadId ?? null} />
                </ResizablePanel>
            </ResizablePanelGroup>
        </div>
    );
}

type ThreadListQuery = {
    readonly user: AdminImessageFilterOptionRead | null;
    readonly organization: AdminImessageFilterOptionRead | null;
    readonly threadState: ThreadStateFilterOption | null;
    readonly limit: number;
};

type FilterOption = {
    readonly id: string;
    readonly name: string;
    readonly internal_name?: string | null;
};

type ThreadStateFilterOption = {
    readonly id: 'started' | 'not_started';
    readonly name: string;
};

const THREAD_STATE_FILTER_OPTIONS: ReadonlyArray<ThreadStateFilterOption> = [
    { id: 'started', name: 'Direct thread started' },
    { id: 'not_started', name: 'Direct thread not started' },
];

function filterOptionLabel(option: FilterOption): string {
    return option.internal_name?.trim() || option.name;
}

type StaffInspectorTarget = {
    readonly userId: string;
    readonly organizationId: string;
};

function ThreadList({
    selectedThreadId,
}: {
    readonly selectedThreadId?: string;
}) {
    const router = useRouter();
    const [provisionDialogOpen, setProvisionDialogOpen] = useState(false);
    const [inspectorTarget, setInspectorTarget] =
        useState<StaffInspectorTarget | null>(null);
    // Filters and limit live in one state object so a filter change resets
    // the limit in the same render, instead of firing a request at the old
    // (possibly 500-row) limit first. Storing the whole selected option
    // keeps the combobox label stable even if the option later drops out
    // of a refreshed filter-options response.
    const [query, setQuery] = useState<ThreadListQuery>({
        user: null,
        organization: null,
        threadState: null,
        limit: THREAD_PAGE_SIZE,
    });
    const threadStarted = query.threadState
        ? query.threadState.id === 'started'
        : undefined;
    const { data, error, isValidating, mutate } = useAdminImessageThreads({
        user_id: query.user?.id,
        organization_id: query.organization?.id,
        thread_started: threadStarted,
        limit: query.limit,
    });
    const { data: filterOptions, mutate: mutateFilterOptions } =
        useAdminImessageFilterOptions();
    const items = data?.items ?? [];
    const total = data?.total ?? 0;
    const hasFilters =
        query.user !== null ||
        query.organization !== null ||
        query.threadState !== null;
    const nowMs = Date.now();

    const getThreadNavigation = (targetThreadId: string) => {
        const routeOptions = {
            to: '/admin/imessage',
            search: { threadId: targetThreadId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <Fragment>
            <div className="border-border border-b p-4">
                <div className="flex items-center justify-between gap-2">
                    <h2 className="text-lg font-semibold">iMessage</h2>
                    <div className="flex items-center gap-2">
                        <Button
                            variant="default"
                            size="sm"
                            onClick={() => setProvisionDialogOpen(true)}
                        >
                            <UserPlus className="h-4 w-4" />
                            Provision
                        </Button>
                        <Button asChild={true} variant="outline" size="sm">
                            <Link to="/admin/imessage-requests">
                                <Lightbulb className="h-4 w-4" />
                                Requests
                            </Link>
                        </Button>
                    </div>
                </div>
                <p className="text-muted-foreground mt-1 text-sm">
                    {total} conversations and enrolled staff, most recent first
                </p>
                <div className="mt-3 flex flex-col gap-2">
                    <FilterCombobox
                        options={filterOptions?.users ?? []}
                        value={query.user}
                        onValueChange={(option) =>
                            setQuery((current) => ({
                                ...current,
                                user: option,
                                limit: THREAD_PAGE_SIZE,
                            }))
                        }
                        allLabel="All people"
                        searchPlaceholder="Filter by name…"
                        icon={UserRound}
                    />
                    <FilterCombobox
                        options={filterOptions?.organizations ?? []}
                        value={query.organization}
                        onValueChange={(option) =>
                            setQuery((current) => ({
                                ...current,
                                organization: option,
                                limit: THREAD_PAGE_SIZE,
                            }))
                        }
                        allLabel="All accounts"
                        searchPlaceholder="Filter by account…"
                        icon={Building2}
                    />
                    <FilterCombobox
                        options={THREAD_STATE_FILTER_OPTIONS}
                        value={query.threadState}
                        onValueChange={(option) =>
                            setQuery((current) => ({
                                ...current,
                                threadState: option,
                                limit: THREAD_PAGE_SIZE,
                            }))
                        }
                        allLabel="All direct thread states"
                        searchPlaceholder="Filter by direct thread state…"
                        icon={MessageCircle}
                    />
                </div>
            </div>
            {provisionDialogOpen ? (
                <StaffImessageProvisionDialog
                    open={true}
                    onOpenChange={setProvisionDialogOpen}
                    onProvisioned={async () => {
                        await Promise.all([mutate(), mutateFilterOptions()]);
                    }}
                />
            ) : null}
            <ScrollArea className="min-h-0 flex-1 overflow-auto">
                {!data ? (
                    error ? (
                        <div className="text-destructive p-4 text-sm">
                            Failed to load iMessage conversations.
                        </div>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading…</span>
                        </div>
                    )
                ) : items.length === 0 ? (
                    <div className="text-muted-foreground flex h-32 flex-col items-center justify-center gap-2">
                        <MessageCircle className="h-6 w-6" />
                        <span className="text-sm">
                            {hasFilters
                                ? 'No matching iMessage staff'
                                : 'No iMessage staff'}
                        </span>
                    </div>
                ) : (
                    items.map((item) => (
                        <ImessageListItemRow
                            key={`${item.item_type}-${item.id}`}
                            item={item}
                            selectedThreadId={selectedThreadId}
                            nowMs={nowMs}
                            getThreadNavigation={getThreadNavigation}
                            onInspect={(target) => setInspectorTarget(target)}
                        />
                    ))
                )}
                {data &&
                items.length < total &&
                query.limit < MAX_THREAD_LIST_LIMIT ? (
                    <div className="p-3">
                        <Button
                            variant="outline"
                            size="sm"
                            className="w-full"
                            disabled={isValidating}
                            onClick={() =>
                                setQuery((current) => ({
                                    ...current,
                                    limit: Math.min(
                                        current.limit + THREAD_PAGE_SIZE,
                                        MAX_THREAD_LIST_LIMIT,
                                    ),
                                }))
                            }
                        >
                            Load more ({items.length} of {total})
                        </Button>
                    </div>
                ) : null}
            </ScrollArea>
            <StaffImessageInspectorSheet
                open={inspectorTarget !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setInspectorTarget(null);
                    }
                }}
                userId={inspectorTarget?.userId ?? ''}
                organizationId={inspectorTarget?.organizationId ?? ''}
            />
        </Fragment>
    );
}

function ImessageListItemRow({
    item,
    selectedThreadId,
    nowMs,
    getThreadNavigation,
    onInspect,
}: {
    readonly item: AdminImessageListItemRead;
    readonly selectedThreadId?: string;
    readonly nowMs: number;
    readonly getThreadNavigation: (threadId: string) => {
        readonly href: string;
        readonly navigate: () => void;
    };
    readonly onInspect: (target: StaffInspectorTarget) => void;
}) {
    const content = (
        <Fragment>
            <ThreadAvatar name={item.user_name} />
            <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                    <span className="truncate font-medium">
                        {item.user_name ?? 'Unknown user'}
                    </span>
                    <span className="text-muted-foreground shrink-0 text-xs">
                        {shortTimeAgo(
                            item.last_activity_at,
                            nowMs,
                            item.organization_timezone,
                        )}
                    </span>
                </div>
                <div className="text-muted-foreground truncate text-sm">
                    {previewText(item)}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5">
                    <span className="text-muted-foreground min-w-0 truncate text-xs">
                        {item.organization_name}
                    </span>
                    <ImessageBadges item={item} />
                </div>
            </div>
        </Fragment>
    );
    const className = cn(
        'border-border flex w-full cursor-pointer items-start gap-3 border-b px-4 py-3 text-left transition-colors hover:bg-muted/50',
        item.thread_id === selectedThreadId && 'bg-muted',
    );

    if (item.thread_id) {
        return (
            <div
                className={className}
                {...getNavigableElementProps(
                    getThreadNavigation(item.thread_id),
                )}
            >
                {content}
            </div>
        );
    }
    return (
        <button
            type="button"
            className={className}
            title="View iMessage settings"
            onClick={() =>
                onInspect({
                    userId: item.user_id,
                    organizationId: item.organization_id,
                })
            }
        >
            {content}
        </button>
    );
}

function FilterCombobox<Option extends FilterOption>({
    options,
    value,
    onValueChange,
    allLabel,
    searchPlaceholder,
    icon: Icon,
}: {
    readonly options: ReadonlyArray<Option>;
    readonly value: Option | null;
    readonly onValueChange: (value: Option | null) => void;
    readonly allLabel: string;
    readonly searchPlaceholder: string;
    readonly icon: LucideIcon;
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            displayValue={
                value ? (
                    <span className="flex items-center gap-2">
                        <Icon className="h-4 w-4 shrink-0" />
                        <span className="truncate">
                            {filterOptionLabel(value)}
                        </span>
                    </span>
                ) : undefined
            }
            placeholder={
                <span className="flex items-center gap-2">
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{allLabel}</span>
                </span>
            }
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder={searchPlaceholder}
            triggerClassName="h-8 w-full"
        >
            <CommandEmpty>No matches found.</CommandEmpty>
            <CommandGroup>
                <CommandItem
                    onSelect={() => {
                        onValueChange(null);
                        setOpen(false);
                    }}
                >
                    <Check
                        className={cn(
                            'mr-2 h-4 w-4',
                            value === null ? 'opacity-100' : 'opacity-0',
                        )}
                    />
                    {allLabel}
                </CommandItem>
                {options.map((option) => (
                    <CommandItem
                        key={option.id}
                        value={`${option.name} ${option.internal_name ?? ''} ${option.id}`}
                        onSelect={() => {
                            onValueChange(option);
                            setOpen(false);
                        }}
                    >
                        <Check
                            className={cn(
                                'mr-2 h-4 w-4',
                                value?.id === option.id
                                    ? 'opacity-100'
                                    : 'opacity-0',
                            )}
                        />
                        <span className="truncate">
                            {filterOptionLabel(option)}
                        </span>
                    </CommandItem>
                ))}
            </CommandGroup>
        </Combobox>
    );
}

function ThreadAvatar({ name }: { readonly name: string | null }) {
    return (
        <div className="bg-muted text-muted-foreground flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-medium">
            {userInitials(name)}
        </div>
    );
}

function ImessageBadges({
    item,
}: {
    readonly item: AdminImessageListItemRead | AdminImessageThreadRead;
}) {
    return (
        <Fragment>
            {item.run_status ? (
                <Badge
                    className={cn(
                        'px-1.5 py-0 text-[10px]',
                        RUN_STATUS_COLORS[item.run_status] ??
                            'bg-muted text-foreground',
                    )}
                >
                    {item.run_status}
                </Badge>
            ) : null}
            {!item.thread_started ? (
                <Badge
                    variant="outline"
                    className="border-amber-300 bg-amber-50 px-1.5 py-0 text-[10px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/50 dark:text-amber-300"
                >
                    {item.chat_kind === 'direct'
                        ? 'awaiting first reply'
                        : 'no direct thread'}
                </Badge>
            ) : null}
            {item.chat_kind === 'group' ? (
                <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
                    group
                </Badge>
            ) : null}
            {'item_type' in item && item.item_type === 'staff' ? (
                <Badge
                    className={cn(
                        'px-1.5 py-0 text-[10px]',
                        ENROLLMENT_STATUS_COLORS[item.enrollment_status] ??
                            'bg-muted text-foreground',
                    )}
                >
                    {item.enrollment_status}
                </Badge>
            ) : null}
        </Fragment>
    );
}

function ThreadDetail({ threadId }: { readonly threadId: string | null }) {
    const { data, isLoading, error } = useAdminImessageThread(threadId);
    const [inspectorOpen, setInspectorOpen] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const lastSeenThreadIdRef = useRef<string | null>(null);
    // Whether the reader was near the bottom, captured from scroll events —
    // by the time the follow effect runs, new messages have already grown
    // scrollHeight, so measuring there would misread an at-bottom reader
    // as scrolled up whenever the new content is taller than the
    // threshold.
    const wasNearBottomRef = useRef(true);
    const loadedMessages = data?.messages;
    const lastMessageId =
        loadedMessages && loadedMessages.length > 0
            ? (loadedMessages[loadedMessages.length - 1]?.id ?? null)
            : null;

    const handleTranscriptScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        wasNearBottomRef.current =
            container.scrollHeight -
                container.scrollTop -
                container.clientHeight <
            FOLLOW_SCROLL_THRESHOLD_PX;
    };

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }
        const isNewThread = lastSeenThreadIdRef.current !== threadId;
        lastSeenThreadIdRef.current = threadId;
        if (isNewThread || wasNearBottomRef.current) {
            container.scrollTop = container.scrollHeight;
            wasNearBottomRef.current = true;
        }
    }, [threadId, lastMessageId]);

    if (!threadId) {
        return (
            <div className="text-muted-foreground flex h-full flex-col items-center justify-center gap-2">
                <MessageCircle className="h-8 w-8" />
                <span className="text-sm">
                    Select a conversation to view messages
                </span>
            </div>
        );
    }
    if (isLoading) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Loading…</span>
            </div>
        );
    }
    if (error || !data) {
        return (
            <div className="text-destructive flex h-full items-center justify-center text-sm">
                Failed to load this conversation.
            </div>
        );
    }

    const { thread, messages } = data;

    return (
        <div className="flex h-full min-h-0 flex-col">
            <div className="border-border flex items-center gap-3 border-b px-6 py-3">
                <button
                    type="button"
                    title="View iMessage settings"
                    onClick={() => setInspectorOpen(true)}
                    className="hover:bg-muted/50 -mx-2 -my-1 flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-md px-2 py-1 text-left transition-colors"
                >
                    <ThreadAvatar name={thread.user_name} />
                    <div className="min-w-0 flex-1">
                        <div className="decoration-muted-foreground/60 truncate font-medium underline underline-offset-2">
                            {thread.user_name ?? 'Unknown user'}
                        </div>
                        <div className="text-muted-foreground truncate text-sm">
                            {thread.organization_name}
                            {thread.user_email ? ` · ${thread.user_email}` : ''}
                        </div>
                    </div>
                </button>
                <div className="flex shrink-0 items-center gap-1.5">
                    <ImessageBadges item={thread} />
                </div>
            </div>
            <StaffImessageInspectorSheet
                open={inspectorOpen}
                onOpenChange={setInspectorOpen}
                userId={thread.user_id}
                organizationId={thread.organization_id}
            />
            <div
                ref={scrollContainerRef}
                onScroll={handleTranscriptScroll}
                className="min-h-0 flex-1 overflow-y-auto px-6 py-4"
            >
                {messages.length === 0 ? (
                    <div className="text-muted-foreground flex h-full items-center justify-center text-sm">
                        No messages yet
                    </div>
                ) : (
                    <div className="space-y-2.5">
                        {messages.map((message, index) => {
                            const previous =
                                index > 0 ? messages[index - 1] : undefined;
                            const dayKey = formatInTimeZone(
                                new Date(message.occurred_at),
                                thread.organization_timezone,
                                'yyyy-MM-dd',
                            );
                            const previousDayKey = previous
                                ? formatInTimeZone(
                                      new Date(previous.occurred_at),
                                      thread.organization_timezone,
                                      'yyyy-MM-dd',
                                  )
                                : null;
                            return (
                                <Fragment key={message.id}>
                                    {dayKey !== previousDayKey && (
                                        <div className="text-muted-foreground py-2 text-center text-xs">
                                            {formatInTimeZone(
                                                new Date(message.occurred_at),
                                                thread.organization_timezone,
                                                'MMMM d, yyyy',
                                            )}
                                        </div>
                                    )}
                                    <MessageBubble
                                        message={message}
                                        timezone={thread.organization_timezone}
                                    />
                                </Fragment>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

function MessageBubble({
    message,
    timezone,
}: {
    readonly message: AdminImessageThreadMessageRead;
    readonly timezone: string;
}) {
    const isOutbound = message.direction === 'outbound';
    return (
        <div
            className={cn(
                'flex flex-col',
                isOutbound ? 'items-end' : 'items-start',
            )}
        >
            <div
                title={formatInTimeZone(
                    new Date(message.occurred_at),
                    timezone,
                    'PPpp zzz',
                )}
                className={cn(
                    'max-w-[70%] rounded-2xl px-3 py-1.5 text-sm break-words whitespace-pre-wrap',
                    isOutbound ? 'bg-blue-500 text-white' : 'bg-muted',
                )}
            >
                {message.body_text ?? (
                    <span className="italic opacity-70">No text</span>
                )}
            </div>
            <span className="text-muted-foreground mt-1 px-1 text-xs">
                {formatInTimeZone(
                    new Date(message.occurred_at),
                    timezone,
                    'h:mm a',
                )}
            </span>
        </div>
    );
}

const ENROLLMENT_STATUS_COLORS: Record<string, string> = {
    active: 'bg-green-100 text-green-800 dark:bg-green-950/50 dark:text-green-300',
    configured:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
    opted_out: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
};

function formatNotificationTypeLabel(value: string): string {
    const text = value.replace(/_/g, ' ');
    return text.charAt(0).toUpperCase() + text.slice(1);
}

function capabilityText(value: boolean | null | undefined): string {
    if (value === null || value === undefined) {
        return 'Unknown';
    }
    return value ? 'Yes' : 'No';
}

function InspectorRow({
    label,
    children,
}: {
    readonly label: string;
    readonly children: ReactNode;
}) {
    return (
        <div className="flex items-baseline justify-between gap-3 text-sm">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="min-w-0 text-right break-words">{children}</span>
        </div>
    );
}

function StaffImessageInspectorSheet({
    open,
    onOpenChange,
    userId,
    organizationId,
}: {
    readonly open: boolean;
    readonly onOpenChange: (open: boolean) => void;
    readonly userId: string;
    readonly organizationId: string;
}) {
    const {
        data: diagnostics,
        error,
        mutate: mutateDiagnostics,
    } = useAdminImessageStaffDiagnostics(open ? userId : null, organizationId);
    const { data: organization, mutate: mutateOrganization } =
        useAdminOrganization(open ? organizationId : null);
    const [savingFlagKey, setSavingFlagKey] = useState<string | null>(null);

    async function toggleOrgFeature(featureKey: string, checked: boolean) {
        if (!organization) {
            return;
        }
        setSavingFlagKey(featureKey);
        try {
            await getApiClient().put({
                url: '/admin/organizations/{organization_id}',
                pathParams: { organization_id: organizationId },
                body: {
                    features: {
                        ...organization.features,
                        [featureKey]: checked,
                    },
                },
            });
            toast.success('Feature flag updated', {
                persistNotification: false,
            });
        } catch {
            toast.error('Failed to update feature flag', {
                persistNotification: false,
            });
        } finally {
            setSavingFlagKey(null);
            await Promise.all([mutateDiagnostics(), mutateOrganization()]);
        }
    }

    const membership = diagnostics?.membership;
    const enrollment = diagnostics?.enrollment ?? null;
    const missingFlags =
        diagnostics?.flags.filter((flag) => flag.required && !flag.enabled) ??
        [];

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="flex w-full flex-col sm:max-w-xl">
                <SheetHeader className="border-border border-b">
                    <SheetTitle>iMessage settings</SheetTitle>
                    <SheetDescription>
                        {diagnostics && membership
                            ? `${diagnostics.user.name ?? diagnostics.user.email} · ${membership.organization_name}`
                            : 'Account, feature flags, notifications, and budget'}
                    </SheetDescription>
                </SheetHeader>
                {!diagnostics || !membership ? (
                    error ? (
                        <div className="text-destructive p-4 text-sm">
                            Failed to load iMessage settings.
                        </div>
                    ) : (
                        <div className="text-muted-foreground flex h-32 items-center justify-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading…</span>
                        </div>
                    )
                ) : (
                    <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-4 pt-0">
                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">Account</h3>
                            <InspectorRow label="Name">
                                {diagnostics.user.name ?? '—'}
                            </InspectorRow>
                            <InspectorRow label="Email">
                                {diagnostics.user.email}
                            </InspectorRow>
                            <InspectorRow label="Organization">
                                {membership.organization_name}
                            </InspectorRow>
                            <InspectorRow label="Org member">
                                {membership.is_member
                                    ? [membership.role, membership.title]
                                          .filter(Boolean)
                                          .join(' · ') || 'Member'
                                    : 'No org member profile'}
                            </InspectorRow>
                            <InspectorRow label="Lifecycle stage">
                                {membership.customer_lifecycle_stage ??
                                    'Not set'}
                            </InspectorRow>
                            <InspectorRow label="Assigned clients only">
                                {membership.notify_only_assigned_coordinators
                                    ? 'Yes'
                                    : 'No'}
                            </InspectorRow>
                            <InspectorRow label="Enrollment">
                                <Badge
                                    className={cn(
                                        'px-1.5 py-0 text-[10px]',
                                        enrollment
                                            ? (ENROLLMENT_STATUS_COLORS[
                                                  enrollment.status
                                              ] ?? 'bg-muted text-foreground')
                                            : 'bg-muted text-foreground',
                                    )}
                                >
                                    {enrollment
                                        ? enrollment.status
                                        : 'not enrolled'}
                                </Badge>
                            </InspectorRow>
                            {enrollment ? (
                                <Fragment>
                                    <InspectorRow label="Phone">
                                        {enrollment.phone_e164}
                                    </InspectorRow>
                                    <InspectorRow label="Phoebe number">
                                        {enrollment.linq_destination_number}
                                    </InspectorRow>
                                    <InspectorRow label="Opted in">
                                        {enrollment.opted_in_at
                                            ? formatInTimeZone(
                                                  new Date(
                                                      enrollment.opted_in_at,
                                                  ),
                                                  membership.organization_timezone,
                                                  'PPp',
                                              )
                                            : '—'}
                                    </InspectorRow>
                                    {enrollment.opted_out_at ? (
                                        <InspectorRow label="Opted out">
                                            {formatInTimeZone(
                                                new Date(
                                                    enrollment.opted_out_at,
                                                ),
                                                membership.organization_timezone,
                                                'PPp',
                                            )}
                                        </InspectorRow>
                                    ) : null}
                                    <InspectorRow label="iMessage capable">
                                        {capabilityText(
                                            enrollment.imessage_capable,
                                        )}
                                    </InspectorRow>
                                    <InspectorRow label="RCS capable">
                                        {capabilityText(enrollment.rcs_capable)}
                                    </InspectorRow>
                                    <InspectorRow label="SMS notifications">
                                        {enrollment.sms_notification_routing_enabled
                                            ? 'Moved over to iMessage'
                                            : 'Kept on SMS'}
                                    </InspectorRow>
                                    <InspectorRow label="Capability checked">
                                        {enrollment.capability_checked_at
                                            ? formatInTimeZone(
                                                  new Date(
                                                      enrollment.capability_checked_at,
                                                  ),
                                                  membership.organization_timezone,
                                                  'PPp',
                                              )
                                            : 'Never'}
                                    </InspectorRow>
                                    {enrollment.capability_error ? (
                                        <InspectorRow label="Capability error">
                                            {enrollment.capability_error}
                                        </InspectorRow>
                                    ) : null}
                                </Fragment>
                            ) : null}
                        </section>
                        <section className="space-y-3">
                            <div className="flex items-center justify-between">
                                <h3 className="text-sm font-semibold">
                                    Feature flags & gates
                                </h3>
                                {missingFlags.length === 0 ? (
                                    <span className="text-xs text-green-700 dark:text-green-400">
                                        All required gates on
                                    </span>
                                ) : (
                                    <span className="text-destructive text-xs">
                                        {missingFlags.length} missing
                                    </span>
                                )}
                            </div>
                            {diagnostics.flags.map((flag) => (
                                <div
                                    key={flag.key}
                                    className="flex items-start justify-between gap-3"
                                >
                                    <div className="flex min-w-0 items-start gap-2">
                                        {flag.required ? (
                                            flag.enabled ? (
                                                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600 dark:text-green-500" />
                                            ) : (
                                                <XCircle className="text-destructive mt-0.5 h-4 w-4 shrink-0" />
                                            )
                                        ) : (
                                            <Info className="text-muted-foreground mt-0.5 h-4 w-4 shrink-0" />
                                        )}
                                        <div className="min-w-0">
                                            <div className="text-sm font-medium">
                                                {flag.label}
                                            </div>
                                            <p className="text-muted-foreground text-xs">
                                                {flag.description}
                                            </p>
                                        </div>
                                    </div>
                                    {flag.org_feature ? (
                                        <Switch
                                            checked={flag.enabled}
                                            disabled={
                                                !organization ||
                                                savingFlagKey !== null
                                            }
                                            onCheckedChange={(checked) =>
                                                void toggleOrgFeature(
                                                    flag.key,
                                                    checked,
                                                )
                                            }
                                            aria-label={`Toggle ${flag.label}`}
                                        />
                                    ) : null}
                                </div>
                            ))}
                        </section>
                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">
                                SMS notifications
                            </h3>
                            {diagnostics.sms_notification_types.length === 0 ? (
                                <p className="text-muted-foreground text-sm">
                                    No SMS notification types enabled.
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-1.5">
                                    {diagnostics.sms_notification_types.map(
                                        (type) => (
                                            <Badge
                                                key={type}
                                                variant="outline"
                                                className="px-1.5 py-0.5 text-xs"
                                            >
                                                {formatNotificationTypeLabel(
                                                    type,
                                                )}
                                            </Badge>
                                        ),
                                    )}
                                </div>
                            )}
                        </section>
                        <section className="space-y-2">
                            <h3 className="text-sm font-semibold">
                                Notification budget
                            </h3>
                            <InspectorRow
                                label={`Sends in last ${diagnostics.budget.window_minutes} min`}
                            >
                                {diagnostics.budget.sends_in_current_window} of{' '}
                                {diagnostics.budget.max_sends_per_hour}
                                {diagnostics.budget.over_budget_now ? (
                                    <Badge className="ml-2 bg-red-100 px-1.5 py-0 text-[10px] text-red-800 dark:bg-red-950/50 dark:text-red-300">
                                        over budget
                                    </Badge>
                                ) : null}
                            </InspectorRow>
                            <p className="text-muted-foreground text-xs">
                                The budget below is an org-wide setting for{' '}
                                {membership.organization_name}; changing it
                                affects every enrolled staff member.
                            </p>
                            {organization ? (
                                <StaffImessageNotificationBudgetSection
                                    organization={organization}
                                    onSaved={() => void mutateDiagnostics()}
                                />
                            ) : null}
                        </section>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
